import type { CSSProperties, FC, ReactNode } from 'react'
import type { TileLook } from '@/components/landing/mockups/media-tile'

import { cn } from '@/lib/utils'
import MediaTile from '@/components/landing/mockups/media-tile'
import { TEAL_TONE, photos } from '@/constants/media'

export type LayoutId = 'two' | 'four' | 'twelve' | 'manga'

interface Rect {
    x: number
    y: number
    w: number
    h: number
    clip?: string
}

const FULL_CLIP = 'polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)'
const GRID_GAP = 0.3

// Four Manga panels split by two slanted dividers that meet at (53.43, 47.59).
export const MANGA_PANELS: Rect[] = [
    { x: 0, y: 0, w: 62, h: 54, clip: 'polygon(0% 0%, 100% 0%, 86.18% 88.13%, 0% 100%)' },
    { x: 53.43, y: 0, w: 46.57, h: 47.59, clip: 'polygon(18.4% 0%, 100% 0%, 100% 88.25%, 0% 100%)' },
    { x: 0, y: 47.59, w: 53.43, h: 52.41, clip: 'polygon(0% 12.23%, 100% 0%, 82.35% 100%, 0% 100%)' },
    { x: 44, y: 42, w: 56, h: 58, clip: 'polygon(16.84% 9.64%, 100% 0%, 100% 100%, 0% 100%)' }
]

const cell = (cols: number, rows: number, col: number, row: number): Rect => ({
    x: (100 / cols) * col,
    y: (100 / rows) * row,
    w: 100 / cols,
    h: 100 / rows
})

const SLOTS: TileLook[] = [
    { src: photos.sportsCar, position: '55% 58%' },
    { src: photos.concert, position: '45% 55%' },
    { src: photos.parkour, position: '62% 38%' },
    { src: photos.eagle, position: '70% 42%' },
    { src: photos.surf, position: '62% 50%', filter: TEAL_TONE },
    { src: photos.dancer, position: '35% 55%' },
    { src: photos.breakdance, position: '50% 38%' },
    { src: photos.neonCity, position: '72% 32%' },
    { src: photos.waterfall, position: '35% 45%' },
    { src: photos.concert, position: '18% 22%', zoom: 1.7, filter: 'hue-rotate(35deg) saturate(1.2)' },
    { src: photos.breakdance, position: '50% 26%', zoom: 2, mirror: true },
    { src: photos.surf, position: '61% 46%', zoom: 1.9 }
]

const TWELVE_CELLS: [number, number][] = [
    [1, 0], [2, 2], [3, 1], [0, 1],
    [0, 0], [3, 0], [1, 1], [2, 1],
    [0, 2], [2, 0], [3, 2], [1, 2]
]

const TWELVE: Rect[] = TWELVE_CELLS.map(([col, row]) => cell(4, 3, col, row))

const LAYOUTS: Record<LayoutId, Rect[]> = {
    two: [cell(2, 1, 0, 0), cell(2, 1, 1, 0)],
    four: [cell(2, 2, 0, 0), cell(2, 2, 1, 0), cell(2, 2, 0, 1), cell(2, 2, 1, 1)],
    twelve: TWELVE,
    manga: MANGA_PANELS
}

const DRIFTS: [string, string][] = [
    ['-2%', '-1.5%'],
    ['2%', '-1%'],
    ['-1.5%', '1.5%'],
    ['1.5%', '1.5%']
]

export const LAYOUT_META: Record<LayoutId, { status: string, title: string, description: string }> = {
    two: {
        status: '2 views · Free',
        title: 'Two videos side by side',
        description: 'Illustrative view: two independent videos side by side on a phone'
    },
    four: {
        status: '4 views · Free',
        title: 'Four videos on one screen',
        description: 'Illustrative view: four videos on a phone screen'
    },
    twelve: {
        status: '12 views · PRO',
        title: 'Twelve views on one screen',
        description: 'Illustrative view: twelve video views on one screen'
    },
    manga: {
        status: 'Manga · 4 panels',
        title: 'Four Manga panels',
        description: 'Illustrative view: four Manga panels with diagonal dividers'
    }
}

const tileStyle = (rect: Rect, gap: number): CSSProperties => ({
    left: `calc(${rect.x}% + ${gap}cqw)`,
    top: `calc(${rect.y}% + ${gap}cqw)`,
    width: `calc(${rect.w}% - ${gap * 2}cqw)`,
    height: `calc(${rect.h}% - ${gap * 2}cqw)`,
    clipPath: rect.clip ?? FULL_CLIP
})

interface MangaGuttersProps {
    visible?: boolean
}

// White gutters drawn over the panel seams, in the same percentage space as the panels.
export const MangaGutters: FC<MangaGuttersProps> = ({ visible = true }): ReactNode => {
    return (
        <svg
            viewBox='0 0 100 100'
            preserveAspectRatio='none'
            aria-hidden='true'
            className={cn(
                'pointer-events-none absolute inset-0 z-10 h-full w-full transition-opacity duration-500',
                visible ? 'opacity-100' : 'opacity-0'
            )}
        >
            <polygon points='61.45,0 62.55,0 44.55,100 43.45,100' fill='#f8fafc' />
            <polygon points='0,52.75 100,40.75 100,43.25 0,55.25' fill='#f8fafc' />
        </svg>
    )
}

interface LayoutScreenProps {
    layout: LayoutId
    motion?: boolean
    eager?: boolean
}

const LayoutScreen: FC<LayoutScreenProps> = ({ layout, motion = false, eager = false }): ReactNode => {
    const rects = LAYOUTS[layout]
    const gap = layout === 'manga' ? 0 : GRID_GAP

    return (
        <div className='absolute inset-0'>
            {SLOTS.map((look, index) => {
                const rect = rects[index]
                const visible = rect !== undefined

                return (
                    <div
                        key={index}
                        className={cn(
                            'absolute transition-[left,top,width,height,clip-path,opacity] duration-700 ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none',
                            visible ? 'opacity-100' : 'opacity-0'
                        )}
                        style={tileStyle(rect ?? TWELVE[index], gap)}
                    >
                        <MediaTile
                            {...look}
                            motion={motion}
                            delay={index * 1.7}
                            drift={DRIFTS[index % DRIFTS.length]}
                            eager={eager}
                        />
                    </div>
                )
            })}

            <MangaGutters visible={layout === 'manga'} />
        </div>
    )
}

interface MangaScreenProps {
    looks: TileLook[]
}

export const MangaScreen: FC<MangaScreenProps> = ({ looks }): ReactNode => {
    return (
        <div className='absolute inset-0'>
            {MANGA_PANELS.map((panel, index) => (
                <div key={index} className='absolute' style={tileStyle(panel, 0)}>
                    {looks[index] && <MediaTile {...looks[index]} />}
                </div>
            ))}

            <MangaGutters />
        </div>
    )
}

export default LayoutScreen
